import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Panel, StatusBadge } from "@/components/app/ui";
import { TechPicker } from "@/components/app/TechPicker";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export function ConsentPanel({ isAdmin }) {
  const [c, setC] = useState(null);
  const [w, setW] = useState(false);
  const [reason, setReason] = useState("");
  const [del, setDel] = useState(false);
  const load = () => api.get("/org/consent").then((r) => setC(r.data));
  useEffect(() => { load(); }, []);
  const withdraw = async () => {
    if (!window.confirm("Withdraw authorisation? Uploads, AI analysis, scans and Copilot will stop until an admin accepts again.")) return;
    try { await api.post("/org/consent/withdraw", { reason, request_deletion: del }); toast.success("Authorisation withdrawn"); window.location.href = "/consent"; } catch (e) { toast.error(errMsg(e)); }
  };
  if (!c) return null;
  return (
    <Panel title="Consent & Terms" testId="org-consent-panel">
      <div className="mb-2 flex items-center gap-2 text-sm">Processing authorisation: <StatusBadge status={c.ok ? "Accepted" : "Required"} testId="org-consent-status" /></div>
      <div className="mb-3 flex flex-wrap gap-3 text-xs">{Object.entries(c.required_versions).map(([k, v]) => <Link key={k} to={`/legal/${k}`} target="_blank" className="text-blue-700 underline" data-testid={`org-legal-${k}`}>{k} v{v}</Link>)}</div>
      <div className="max-h-40 overflow-y-auto rounded border text-xs" data-testid="org-consent-history">
        {c.history.map((h) => <div key={h.id} className="border-b px-3 py-1.5 last:border-0"><b>{h.status}</b> · {h.user_email}{h.signatory_designation && ` (${h.signatory_designation})`} · {fmtDateTime(h.timestamp)} · disclosure v{h.versions?.disclosure}{h.reason && ` — ${h.reason}`}</div>)}
        {!c.history.length && <div className="px-3 py-2 text-slate-400">No records yet.</div>}
      </div>
      {isAdmin && c.ok && !w && <Button size="sm" variant="ghost" className="mt-2 text-rose-600" onClick={() => setW(true)} data-testid="org-consent-withdraw-open">Withdraw authorisation / request deletion</Button>}
      {w && <div className="mt-3 space-y-2">
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (min 10 characters)" data-testid="org-consent-withdraw-reason" />
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={del} onCheckedChange={(v) => setDel(!!v)} data-testid="org-consent-request-deletion" />Also request deletion of our organisation's data</label>
        <Button size="sm" variant="outline" disabled={reason.trim().length < 10} onClick={withdraw} data-testid="org-consent-withdraw-confirm">Confirm withdrawal</Button></div>}
    </Panel>
  );
}

export function TechPanel({ profile, isAdmin, onSaved }) {
  const [v, setV] = useState({ technologies: profile.technologies || [], other_technologies: profile.other_technologies || [] });
  const save = async () => {
    try { await api.put("/org/profile", { profile: { ...profile, ...v } }); toast.success("Technologies saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Technologies" testId="org-technologies">
      {isAdmin ? <><TechPicker value={v.technologies} other={v.other_technologies} onChange={setV} /><Button size="sm" onClick={save} className="mt-3 bg-blue-600 hover:bg-blue-700" data-testid="org-tech-save">Save technologies</Button></>
        : <div className="text-sm">{[...(profile.technologies || []), ...(profile.other_technologies || [])].join(", ") || "—"}</div>}
    </Panel>
  );
}
